import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Copy, Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";

const vnd = (n) => new Intl.NumberFormat("vi-VN").format(n) + " ₫";

const CopyRow = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/10 py-3 last:border-0">
    <span className="text-xs uppercase tracking-widest text-ink-soft">{label}</span>
    <button
      data-testid={testid}
      onClick={() => {
        navigator.clipboard?.writeText(String(value));
        toast.success(`${label} copied`);
      }}
      className="group flex items-center gap-2 text-end text-sm font-semibold hover:text-brand"
    >
      {value}
      <Copy className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100" />
    </button>
  </div>
);

export const BankTransferDialog = ({ intent, onClose, onApproved }) => {
  const [payer, setPayer] = useState(intent?.payer_default || "");
  const [status, setStatus] = useState(intent?.status || "awaiting_transfer");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus(intent?.status || "awaiting_transfer");
  }, [intent]);

  useEffect(() => {
    if (!intent || status !== "awaiting_review") return;
    let polls = 0;
    const id = setInterval(async () => {
      if (++polls > 120) return clearInterval(id); // stop after ~10 minutes
      try {
        const { data } = await api.get(`/localpay/${intent.ref}`);
        if (data.status !== status) {
          setStatus(data.status);
          if (data.status === "approved") {
            localStorage.setItem(
              "chatmilu.milu.celebrate",
              intent.kind === "donation" ? "your donation" : intent.plan_id || "your plan",
            );
            onApproved?.(data);
          }
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, status]);

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/localpay/${intent.ref}/claim`, {
        payer_name: payer || undefined,
        note: intent.note || undefined,
        public: intent.public !== false,
      });
      setStatus(data.status);
      toast.success("Thanks — we'll confirm your transfer shortly");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!intent) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="bank-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
            <Landmark className="h-5 w-5 text-brand" /> Transfer with any Vietnamese bank app
          </DialogTitle>
        </DialogHeader>

        {status === "approved" ? (
          <div className="py-8 text-center" data-testid="bank-transfer-approved">
            <Check className="mx-auto h-10 w-10 text-forest" />
            <p className="mt-4 font-display text-xl font-bold tracking-tight">Transfer confirmed</p>
            <p className="mt-1 text-sm text-ink-soft">
              {intent.kind === "donation"
                ? "Thank you — your donation is in."
                : "Your plan is active now. Reload the page to see it."}
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl bg-white p-4">
              <img
                data-testid="bank-transfer-qr"
                src={intent.qr_image}
                alt="VietQR code"
                className="mx-auto h-56 w-56"
              />
            </div>
            <p className="text-center text-xs text-ink-soft">
              Open your banking app → scan → the amount and reference fill in automatically.
            </p>

            <div className="rounded-2xl border border-white/10 bg-panel px-4">
              <CopyRow label="Amount" value={vnd(intent.amount_vnd)} testid="bank-copy-amount" />
              <CopyRow label="Bank" value={intent.bank_name} testid="bank-copy-bank" />
              <CopyRow label="Account" value={intent.account_no} testid="bank-copy-account" />
              <CopyRow label="Name" value={intent.account_name} testid="bank-copy-name" />
              <CopyRow label="Reference" value={intent.ref} testid="bank-copy-ref" />
            </div>
            <p className="text-xs text-ink-soft">
              Put <b className="text-ink">{intent.ref}</b> in the transfer message so we can match it — that's
              ${intent.amount_usd} at today's rate.
            </p>

            {status === "awaiting_review" ? (
              <div
                className="rounded-2xl border border-forest/30 bg-forest/10 p-4 text-sm"
                data-testid="bank-transfer-pending"
              >
                <b>Got it — waiting on confirmation.</b>
                <p className="mt-1 text-ink-soft">
                  {intent.auto_confirm
                    ? "Your bank credit is matched automatically, usually within a minute. This page updates itself."
                    : "We check transfers manually, usually within a few hours."}{" "}
                  Keep this reference: <b className="text-ink">{intent.ref}</b>
                </p>
              </div>
            ) : (
              <>
                <Input
                  data-testid="bank-payer-name"
                  placeholder="Name on your bank account (optional)"
                  maxLength={60}
                  value={payer}
                  onChange={(e) => setPayer(e.target.value)}
                  className="min-h-[44px] rounded-xl"
                />
                <Button
                  data-testid="bank-transfer-done"
                  onClick={claim}
                  disabled={busy}
                  className="min-h-[48px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
                >
                  {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                  I've made the transfer
                </Button>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
